import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/context/ThemeContext';

type AnimPhase = 'idle' | 'swimming' | 'transforming' | 'logo' | 'powering' | 'done';

export default function StingrayAnimation() {
  const { theme } = useTheme();
  const light = theme === 'light';
  const [phase, setPhase] = useState<AnimPhase>('idle');
  const [replay, setReplay] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPhase('idle');
    const t0 = setTimeout(() => setPhase('swimming'), 300);
    const t1 = setTimeout(() => setPhase('transforming'), 2800);
    const t2 = setTimeout(() => setPhase('logo'), 3800);
    const t3 = setTimeout(() => setPhase('powering'), 4600);
    const t4 = setTimeout(() => setPhase('done'), 7000);
    return () => { [t0, t1, t2, t3, t4].forEach(clearTimeout); };
  }, [replay]);

  const bg = light ? 'bg-gradient-to-br from-slate-100 via-blue-50 to-cyan-100' : 'bg-gradient-to-br from-[#050D18] via-[#071525] to-[#0A1E35]';
  const waterColor = light ? '#93C5FD' : '#0EA5E9';
  const deepColor = light ? '#BFDBFE' : '#0369A1';
  const accentGlow = '#0EA5E9';

  return (
    <div
      ref={containerRef}
      className={`relative w-full rounded-2xl overflow-hidden ${bg} select-none`}
      style={{ height: 420 }}
    >
      {/* Inline keyframes */}
      <style>{`
        @keyframes sr-swim {
          0%   { transform: translateX(-180px) translateY(0px) scaleX(1); opacity: 0; }
          5%   { opacity: 1; }
          40%  { transform: translateX(0px) translateY(-10px) scaleX(1); opacity: 1; }
          70%  { transform: translateX(60px) translateY(5px) scaleX(1); opacity: 1; }
          85%  { transform: translateX(80px) translateY(0px) scaleX(1); opacity: 1; }
          100% { transform: translateX(80px) translateY(0px) scaleX(1); opacity: 0; }
        }
        @keyframes sr-wave {
          0%   { d: path("M0,60 C80,40 160,80 240,60 C320,40 400,80 480,60 C560,40 640,80 720,60 L720,120 L0,120 Z"); }
          50%  { d: path("M0,70 C80,90 160,50 240,70 C320,90 400,50 480,70 C560,90 640,50 720,70 L720,120 L0,120 Z"); }
          100% { d: path("M0,60 C80,40 160,80 240,60 C320,40 400,80 480,60 C560,40 640,80 720,60 L720,120 L0,120 Z"); }
        }
        @keyframes sr-wave2 {
          0%   { d: path("M0,80 C100,60 200,100 300,80 C400,60 500,100 600,80 C700,60 800,100 900,80 L900,120 L0,120 Z"); }
          50%  { d: path("M0,90 C100,110 200,70 300,90 C400,110 500,70 600,90 C700,110 800,70 900,90 L900,120 L0,120 Z"); }
          100% { d: path("M0,80 C100,60 200,100 300,80 C400,60 500,100 600,80 C700,60 800,100 900,80 L900,120 L0,120 Z"); }
        }
        @keyframes sr-bubble {
          0%   { transform: translateY(0) scale(1); opacity: 0.6; }
          100% { transform: translateY(-80px) scale(0.3); opacity: 0; }
        }
        @keyframes sr-logo-appear {
          0%   { transform: scale(0.2) rotate(-20deg); opacity: 0; filter: blur(12px); }
          60%  { transform: scale(1.15) rotate(3deg); opacity: 1; filter: blur(0px); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; filter: blur(0px); }
        }
        @keyframes sr-bolt {
          0%,100% { opacity: 0; transform: scaleY(0.5) translateX(0); }
          10%,30% { opacity: 1; transform: scaleY(1) translateX(-2px); }
          20%,40% { opacity: 0.7; transform: scaleY(1) translateX(2px); }
          50%      { opacity: 1; transform: scaleY(1.1) translateX(0); }
          70%      { opacity: 0.5; }
        }
        @keyframes sr-ring {
          0%   { transform: scale(0.5); opacity: 0.9; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes sr-glow-pulse {
          0%,100% { opacity: 0.4; transform: scale(1); }
          50%      { opacity: 0.9; transform: scale(1.08); }
        }
        @keyframes sr-particle {
          0%   { transform: translate(0,0) scale(1); opacity: 1; }
          100% { transform: translate(var(--px), var(--py)) scale(0); opacity: 0; }
        }
        @keyframes sr-text-in {
          0%   { opacity: 0; transform: translateY(16px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes sr-fin-wave {
          0%,100% { transform: rotate(-5deg); }
          50%      { transform: rotate(5deg); }
        }
        @keyframes sr-tail-wave {
          0%,100% { transform: rotate(-8deg) translateX(0); }
          50%      { transform: rotate(8deg) translateX(-3px); }
        }
        @keyframes sr-electric-arc {
          0%,100% { opacity: 0; }
          15%,35% { opacity: 1; }
          25%      { opacity: 0.3; }
          50%      { opacity: 0.8; }
          75%      { opacity: 0.2; }
        }
        @keyframes sr-water-glow {
          0%,100% { opacity: 0.15; }
          50%      { opacity: 0.45; }
        }
        @keyframes sr-logo-float {
          0%,100% { transform: translateY(0px); }
          50%      { transform: translateY(-6px); }
        }
      `}</style>

      {/* Deep water background layers */}
      <div className="absolute inset-0">
        {/* Depth gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: light
              ? 'linear-gradient(180deg, rgba(219,234,254,0.3) 0%, rgba(147,197,253,0.5) 60%, rgba(59,130,246,0.3) 100%)'
              : 'linear-gradient(180deg, rgba(7,21,37,0.8) 0%, rgba(3,105,161,0.15) 50%, rgba(2,132,199,0.08) 100%)',
          }}
        />

        {/* Animated water caustics */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: 80 + i * 40,
              height: 80 + i * 40,
              left: `${10 + i * 15}%`,
              top: `${20 + (i % 3) * 20}%`,
              background: `radial-gradient(circle, ${accentGlow}18 0%, transparent 70%)`,
              animation: `sr-water-glow ${2 + i * 0.4}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      {/* Water surface waves (SVG) */}
      <svg
        className="absolute bottom-0 left-0 w-full"
        viewBox="0 0 720 120"
        preserveAspectRatio="none"
        style={{ height: 120 }}
      >
        <path
          style={{
            fill: light ? 'rgba(147,197,253,0.35)' : 'rgba(14,165,233,0.12)',
            animation: 'sr-wave 3s ease-in-out infinite',
          }}
          d="M0,60 C80,40 160,80 240,60 C320,40 400,80 480,60 C560,40 640,80 720,60 L720,120 L0,120 Z"
        />
        <path
          style={{
            fill: light ? 'rgba(96,165,250,0.25)' : 'rgba(14,165,233,0.08)',
            animation: 'sr-wave2 4s ease-in-out infinite',
            animationDelay: '0.5s',
          }}
          d="M0,80 C100,60 200,100 300,80 C400,60 500,100 600,80 C700,60 800,100 900,80 L900,120 L0,120 Z"
        />
      </svg>

      {/* Bubbles */}
      {(phase === 'swimming' || phase === 'transforming') && [...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full border"
          style={{
            width: 4 + (i % 3) * 4,
            height: 4 + (i % 3) * 4,
            left: `${30 + i * 8}%`,
            bottom: `${15 + (i % 4) * 8}%`,
            borderColor: `${accentGlow}60`,
            backgroundColor: `${accentGlow}15`,
            animation: `sr-bubble ${1.5 + i * 0.3}s ease-out infinite`,
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}

      {/* ── STINGRAY FISH SVG ── */}
      {(phase === 'swimming' || phase === 'transforming') && (
        <div
          className="absolute"
          style={{
            left: '50%',
            top: '42%',
            transform: 'translate(-50%, -50%)',
            animation: phase === 'swimming'
              ? 'sr-swim 2.5s cubic-bezier(0.25,0.46,0.45,0.94) forwards'
              : 'none',
            opacity: phase === 'transforming' ? 0 : undefined,
            transition: phase === 'transforming' ? 'opacity 0.8s ease-out' : undefined,
          }}
        >
          <svg width="220" height="140" viewBox="0 0 220 140" fill="none">
            {/* Body glow */}
            <ellipse cx="100" cy="70" rx="75" ry="38"
              fill={accentGlow} fillOpacity="0.12"
              style={{ filter: 'blur(8px)' }}
            />

            {/* Main body — manta ray / stingray shape */}
            <path
              d="M 30 70 C 40 30, 100 20, 150 50 C 170 60, 175 70, 170 80 C 160 100, 100 115, 50 100 C 30 93, 20 82, 30 70 Z"
              fill={light ? '#1E40AF' : '#0EA5E9'}
              fillOpacity={light ? 0.85 : 0.9}
            />
            {/* Body highlight */}
            <path
              d="M 50 55 C 70 35, 120 30, 150 52 C 130 40, 80 38, 55 58 Z"
              fill="white" fillOpacity="0.18"
            />

            {/* Left wing */}
            <path
              d="M 80 65 C 50 30, 10 15, 5 40 C 0 55, 25 70, 80 72 Z"
              fill={light ? '#1D4ED8' : '#38BDF8'}
              fillOpacity="0.85"
              style={{ transformOrigin: '80px 68px', animation: 'sr-fin-wave 0.8s ease-in-out infinite' }}
            />
            {/* Right wing */}
            <path
              d="M 80 75 C 50 100, 8 110, 5 88 C 2 72, 28 68, 80 72 Z"
              fill={light ? '#1D4ED8' : '#38BDF8'}
              fillOpacity="0.85"
              style={{ transformOrigin: '80px 72px', animation: 'sr-fin-wave 0.8s ease-in-out infinite', animationDelay: '0.4s' }}
            />

            {/* Tail */}
            <path
              d="M 165 72 C 185 65, 210 50, 215 55 C 218 58, 205 70, 215 82 C 210 88, 185 78, 165 75 Z"
              fill={light ? '#1E40AF' : '#0EA5E9'}
              fillOpacity="0.8"
              style={{ transformOrigin: '165px 73px', animation: 'sr-tail-wave 0.6s ease-in-out infinite' }}
            />

            {/* Stinger tail */}
            <path
              d="M 168 73 L 215 68 L 218 72 L 215 76 L 168 74 Z"
              fill={light ? '#60A5FA' : '#7DD3FC'}
              fillOpacity="0.9"
              style={{ transformOrigin: '168px 73px', animation: 'sr-tail-wave 0.6s ease-in-out infinite' }}
            />

            {/* Eye */}
            <circle cx="145" cy="62" r="5" fill="white" fillOpacity="0.9" />
            <circle cx="146" cy="62" r="2.5" fill={light ? '#1E3A8A' : '#0C4A6E'} />
            <circle cx="147" cy="61" r="1" fill="white" fillOpacity="0.8" />

            {/* Electric markings on body */}
            <path
              d="M 90 58 L 85 68 L 92 65 L 87 78"
              stroke={light ? '#FCD34D' : '#FDE68A'}
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
              opacity="0.8"
            />
            <path
              d="M 110 55 L 106 63 L 112 61 L 108 72"
              stroke={light ? '#FCD34D' : '#FDE68A'}
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
              opacity="0.6"
            />

            {/* Electric aura around fish */}
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
              <line
                key={i}
                x1={100 + Math.cos(deg * Math.PI / 180) * 45}
                y1={70 + Math.sin(deg * Math.PI / 180) * 28}
                x2={100 + Math.cos(deg * Math.PI / 180) * 58}
                y2={70 + Math.sin(deg * Math.PI / 180) * 36}
                stroke={light ? '#FCD34D' : '#FDE68A'}
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity="0.5"
                style={{ animation: `sr-electric-arc ${0.4 + i * 0.1}s ease-in-out infinite`, animationDelay: `${i * 0.07}s` }}
              />
            ))}
          </svg>
        </div>
      )}

      {/* ── TRANSFORM FLASH ── */}
      {phase === 'transforming' && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ zIndex: 10 }}
        >
          {/* White flash */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at center, rgba(255,255,255,0.95) 0%, rgba(14,165,233,0.4) 40%, transparent 70%)',
              animation: 'sr-ring 0.8s ease-out forwards',
            }}
          />
          {/* Electric rings */}
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="absolute rounded-full border-2"
              style={{
                width: 80 + i * 60,
                height: 80 + i * 60,
                borderColor: accentGlow,
                animation: `sr-ring 0.8s ease-out forwards`,
                animationDelay: `${i * 0.12}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* ── LOGO + ELECTRIC PHASE ── */}
      {(phase === 'logo' || phase === 'powering' || phase === 'done') && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ zIndex: 5 }}
        >
          {/* Glow behind logo */}
          <div
            className="absolute rounded-full"
            style={{
              width: 260,
              height: 260,
              background: `radial-gradient(circle, ${accentGlow}35 0%, ${accentGlow}10 50%, transparent 70%)`,
              animation: 'sr-glow-pulse 2s ease-in-out infinite',
            }}
          />

          {/* Logo */}
          <div
            style={{
              animation: phase === 'logo'
                ? 'sr-logo-appear 0.9s cubic-bezier(0.34,1.56,0.64,1) forwards'
                : 'sr-logo-float 3s ease-in-out infinite',
            }}
          >
            <img
              src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
              alt="Stingray"
              style={{
                height: 72,
                width: 'auto',
                filter: light
                  ? 'brightness(0) saturate(100%) invert(27%) sepia(98%) saturate(1500%) hue-rotate(190deg) brightness(90%)'
                  : 'brightness(0) invert(1)',
                objectFit: 'contain',
              }}
            />
          </div>

          {/* Electric bolts radiating from logo */}
          {(phase === 'powering' || phase === 'done') && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
                const rad = (deg * Math.PI) / 180;
                const len = 80 + (i % 3) * 30;
                const x2 = Math.cos(rad) * len;
                const y2 = Math.sin(rad) * len;
                return (
                  <svg
                    key={i}
                    className="absolute"
                    style={{ width: 400, height: 400, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                    viewBox="-200 -200 400 400"
                  >
                    {/* Jagged lightning bolt */}
                    <polyline
                      points={`0,0 ${x2 * 0.3},${y2 * 0.3 + (i % 2 === 0 ? -8 : 8)} ${x2 * 0.6},${y2 * 0.6 + (i % 2 === 0 ? 6 : -6)} ${x2},${y2}`}
                      stroke={i % 3 === 0 ? '#FDE68A' : accentGlow}
                      strokeWidth={i % 2 === 0 ? 2.5 : 1.5}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        animation: `sr-bolt ${0.3 + (i % 3) * 0.15}s ease-in-out infinite`,
                        animationDelay: `${i * 0.08}s`,
                      }}
                    />
                    {/* Glow copy */}
                    <polyline
                      points={`0,0 ${x2 * 0.3},${y2 * 0.3 + (i % 2 === 0 ? -8 : 8)} ${x2 * 0.6},${y2 * 0.6 + (i % 2 === 0 ? 6 : -6)} ${x2},${y2}`}
                      stroke={accentGlow}
                      strokeWidth={i % 2 === 0 ? 6 : 4}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.2"
                      style={{
                        animation: `sr-bolt ${0.3 + (i % 3) * 0.15}s ease-in-out infinite`,
                        animationDelay: `${i * 0.08}s`,
                      }}
                    />
                  </svg>
                );
              })}

              {/* Expanding rings */}
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="absolute rounded-full border"
                  style={{
                    width: 100,
                    height: 100,
                    borderColor: `${accentGlow}80`,
                    animation: `sr-ring 1.6s ease-out infinite`,
                    animationDelay: `${i * 0.4}s`,
                  }}
                />
              ))}
            </div>
          )}

          {/* Particles */}
          {(phase === 'powering' || phase === 'done') && [...Array(16)].map((_, i) => {
            const angle = (i / 16) * 360;
            const dist = 80 + (i % 4) * 30;
            const px = Math.cos(angle * Math.PI / 180) * dist;
            const py = Math.sin(angle * Math.PI / 180) * dist;
            return (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: 4 + (i % 3) * 2,
                  height: 4 + (i % 3) * 2,
                  backgroundColor: i % 3 === 0 ? '#FDE68A' : accentGlow,
                  left: '50%',
                  top: '50%',
                  marginLeft: -3,
                  marginTop: -3,
                  ['--px' as string]: `${px}px`,
                  ['--py' as string]: `${py}px`,
                  animation: `sr-particle 1.2s ease-out infinite`,
                  animationDelay: `${i * 0.075}s`,
                } as React.CSSProperties}
              />
            );
          })}

          {/* Text below logo */}
          {(phase === 'powering' || phase === 'done') && (
            <div
              className="mt-8 text-center"
              style={{ animation: 'sr-text-in 0.6s ease-out forwards' }}
            >
              <p
                className="text-xs font-black tracking-widest uppercase mb-1"
                style={{ color: accentGlow }}
              >
                Powering the Ocean
              </p>
              <p className={`text-[11px] tracking-wide ${light ? 'text-gray-500' : 'text-white/40'}`}>
                Like the stingray — silent, powerful, electric
              </p>
            </div>
          )}
        </div>
      )}

      {/* Water electric glow when powering */}
      {(phase === 'powering' || phase === 'done') && (
        <div
          className="absolute bottom-0 left-0 w-full"
          style={{
            height: 120,
            background: `linear-gradient(to top, ${accentGlow}20, transparent)`,
            animation: 'sr-water-glow 1.5s ease-in-out infinite',
          }}
        />
      )}

      {/* Replay button */}
      {phase === 'done' && (
        <button
          onClick={() => setReplay((r) => r + 1)}
          className="absolute bottom-5 right-5 flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-all hover:scale-105"
          style={{
            backgroundColor: `${accentGlow}20`,
            border: `1px solid ${accentGlow}50`,
            color: accentGlow,
          }}
        >
          <i className="ri-refresh-line"></i>
          Replay
        </button>
      )}

      {/* Phase label (subtle) */}
      <div className="absolute top-4 left-5">
        <span
          className="text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full"
          style={{
            backgroundColor: `${accentGlow}15`,
            border: `1px solid ${accentGlow}30`,
            color: accentGlow,
          }}
        >
          {phase === 'idle' && 'Initializing...'}
          {phase === 'swimming' && 'Stingray Approaching'}
          {phase === 'transforming' && 'Transforming...'}
          {phase === 'logo' && 'Stingray Marine'}
          {phase === 'powering' && 'Charging the Ocean'}
          {phase === 'done' && 'Powered by Stingray'}
        </span>
      </div>
    </div>
  );
}
